// BEUS-11496 M3 (P0 #8 임시 채택): 오늘 날짜처럼 아직 적재되지 않은 시간이 걸친 조회는 끝 시각을 공개 범위(watermarkAt)에
// 맞춰 자른다. v2는 공개 범위 밖을 요청하면 503(HEATMAP_DATA_NOT_READY)을 주기 때문이다.
//
// - 공개 범위는 `/v2/heatmap/status`에서 aid별로 한 번 읽고 STATUS_TTL_MS 동안 같은 값을 쓴다. 그래서 한 화면의 여러 패널
//   (Summary·Scroll·Positions·Elements)이 같은 끝 시각으로 조회한다. watermark는 늘어나기만 하므로 조금 오래된 값이어도
//   조회 범위가 공개 범위를 넘지 않는다.
// - 자른 뒤 남는 범위가 없거나(오늘 첫 시간이 아직 적재 전) 시작이 공개 범위·cutover보다 앞이면 v2로는 못 받는다.
//   그때는 ok=false와 이유를 돌려주고, 호출하는 쪽이 기존 API를 쓴다(M3 동안. 기존 API를 끄는 M4 전에 빈 화면 안내로 바꾼다).
// - status를 못 읽으면 자르지 않고 원래 범위로 v2를 부른다(범위 밖이면 v2가 503을 주고 기존 API로 돌아간다).

import { getStatusV2 } from '@/api/heatmap-v2'

export const STATUS_TTL_MS = 60 * 1000

const statusCache = new Map() // aid -> { at, promise }

function toMs (iso) {
  return iso ? new Date(iso).getTime() : null
}

function offsetOf (iso) {
  const m = /([+-]\d{2}:\d{2}|Z)$/.exec(iso || '')
  return m ? (m[1] === 'Z' ? '+00:00' : m[1]) : '+00:00'
}

function offsetMinutes (offset) {
  const sign = offset[0] === '-' ? -1 : 1
  return sign * (Number(offset.slice(1, 3)) * 60 + Number(offset.slice(4, 6)))
}

function pad (n) {
  return String(n).padStart(2, '0')
}

// ms를 원래 범위와 같은 offset의 ISO 문자열로(요청 모양을 바꾸지 않으려고).
export function formatWithOffset (ms, offset) {
  const t = new Date(ms + offsetMinutes(offset) * 60000)
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}` +
    `T${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}:${pad(t.getUTCSeconds())}${offset}`
}

// range: { startAt, endAt } (v2 요청 범위), status: `/v2/heatmap/status` result 또는 null.
// 돌려주는 값: { ok: true, range, clipped } 또는 { ok: false, reason }
export function clipRangeToStatus (range, status) {
  if (!status) return { ok: true, range, clipped: false }
  const start = toMs(range.startAt)
  const end = toMs(range.endAt)
  const watermark = toMs(status.watermarkAt)
  const coverage = toMs(status.coverageStart)
  const cutover = toMs(status.cutoverAt)
  if (watermark === null) return { ok: false, reason: 'notReady' }
  if (cutover !== null && start < cutover) return { ok: false, reason: 'legacyRange' }
  if (coverage !== null && start < coverage) return { ok: false, reason: 'beforeCoverage' }
  if (end <= watermark) return { ok: true, range, clipped: false }
  if (watermark <= start) return { ok: false, reason: 'notReady' }
  return { ok: true, range: { ...range, endAt: formatWithOffset(watermark, offsetOf(range.endAt)) }, clipped: true }
}

export function getServingStatus (aid, now = Date.now()) {
  const cached = statusCache.get(aid)
  if (cached && now - cached.at < STATUS_TTL_MS) return cached.promise
  const promise = getStatusV2({ aid }).catch(err => {
    statusCache.delete(aid) // 실패는 남기지 않는다(다음 호출에서 다시 읽음).
    console.warn('[heatmap-v2] status unavailable, range is not clipped:', err.status, err.code || err.message)
    return null
  })
  statusCache.set(aid, { at: now, promise })
  return promise
}

// 요청 range를 공개 범위에 맞춘다. 항상 resolve한다(reject하지 않음).
export function servingRange (aid, range) {
  return getServingStatus(aid).then(status => {
    const clip = clipRangeToStatus(range, status)
    if (clip.clipped) console.info('[heatmap-v2] range end clipped to watermark:', clip.range.endAt)
    return clip
  })
}

// 테스트용
export function clearServingStatusCache () {
  statusCache.clear()
}
